import { Controller } from "@hotwired/stimulus"

// Replaces the browser's `title=` tooltip on `MockedValueHintComponent` with
// a custom popover that also opens on keyboard focus and stays reachable on
// touch devices, where `title=` is silently dropped.
export default class extends Controller {
  static values = { open: Boolean }

  connect() {
    this.tooltipText = this.element.getAttribute("title")
    if (!this.tooltipText) return

    // Drop the native title so we don't render two overlapping tooltips.
    this.element.removeAttribute("title")
    this.element.setAttribute("tabindex", "0")

    this.tooltip = this.#buildTooltip(this.tooltipText)
    this.element.appendChild(this.tooltip)
    this.element.setAttribute("aria-describedby", this.tooltip.id)

    this.element.addEventListener("mouseenter", this.#open)
    this.element.addEventListener("mouseleave", this.#close)
    this.element.addEventListener("focus", this.#open)
    this.element.addEventListener("blur", this.#close)
    // WCAG 1.4.13: content shown on hover/focus must be dismissible without
    // moving the pointer or focus — Escape closes the tooltip in place.
    this.element.addEventListener("keydown", this.#onKeydown)
  }

  disconnect() {
    this.element.removeEventListener("mouseenter", this.#open)
    this.element.removeEventListener("mouseleave", this.#close)
    this.element.removeEventListener("focus", this.#open)
    this.element.removeEventListener("blur", this.#close)
    this.element.removeEventListener("keydown", this.#onKeydown)
  }

  #open = () => this.openValue = true
  #close = () => this.openValue = false

  #onKeydown = (event) => {
    if (event.key === "Escape" && this.openValue) {
      this.#close()
      event.stopPropagation()
    }
  }

  openValueChanged() {
    if (!this.tooltip) return
    this.tooltip.hidden = !this.openValue
  }

  #buildTooltip(text) {
    const id = `dry-run-hint-${Math.random().toString(36).slice(2, 10)}`
    const el = document.createElement("span")
    el.id = id
    el.className = "tooltip"
    el.role = "tooltip"
    el.textContent = text
    el.hidden = true
    return el
  }
}
